/*
 * Copyright (C) 2013 Spanning Cloud Apps.  All rights reserved.
 */
angular
  .module('http.batch', [])
  .constant('BATCH_STATES', {
    FINISHED: 'FINISHED',
    STARTED: 'STARTED'
  })
  /**
   * Service used to access all calls to the batch rest service.
   */
  .factory('httpBatch', function ($http, BATCH_STATES) {
    return {
      getBackupBatchDots,
      getBackupDetails,
      getBackupsByDateRange,
      getBackupsByMonth,
      getBatch,
      getBatchByDate,
      getBatchEvents,
      getBatchLevelRestoreFailures,
      getBatchPrimaryObjectCounts,
      getBatches,
      getCsv,
      getDownloadBatchUrl,
      getEventCountForBatch,
      getLatestBackup,
      getLatestBulkRestoreCsvVerification,
      getLatestBulkRestorePreview,
      getLatestExport,
      getLatestMetadataCompare,
      getLatestObjectRestore,
      getMetadataBatchRecords,
      getNextBatch,
      getPreviousBatch,
      postBatchCancellation
    };

    /**
     * Function called to get a list of finished backups.
     * @param {number} limit The max number of results to be returned.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    function getBatches(limit) {
      return $http
        .get('api/batches', {
          params: {
            limit,
            state: BATCH_STATES.FINISHED,
            type: 'backup'
          }
        })
        .then((res) => res.data);
    }

    /**
     * Function called to retrieve a single batch by batchId.
     *
     * @param {Number} batchId - The batch id of the batch to be retrieved.
     * @returns {Object} An object that is the batchInfo returned from the rest service.
     */
    function getBatch(batchId) {
      return $http.get('api/batch', { params: { batchId } }).then((res) => res.data);
    }

    /**
     * Function called to retrieve a single batch by batchId.
     *
     * @param {Number} batchId - The batch id of the batch to be retrieved.
     * @returns {Promise} - A promise that resolves into a batchInfo returned from the rest service.
     */
    function getNextBatch(batchId) {
      return $http.get('api/batch/next', { params: { batchId } }).then((res) => res.data);
    }

    /**
     * Function called to retrieve a single batch by batchId.
     *
     * @param {Number} batchId - The batch id of the batch to be retrieved.
     * @returns {Promise} - A promise that resolves into a batchInfo returned from the rest service.
     */
    function getPreviousBatch(batchId) {
      return $http.get('api/batch/previous', { params: { batchId } }).then((res) => res.data);
    }

    /**
     * Function called to retrieve a single batch by batchDate.
     *
     * @param {Date} batchDate - The date to query for a batch.
     * @returns {Promise} - A promise that resolves into a batchInfo returned from the rest service.
     */
    function getBatchByDate(batchDate) {
      return $http.get('api/batch/date', { params: { batchDate: batchDate.toISOString() } }).then((res) => res.data);
    }

    /**
     * Function called to retrieve a single batch by batchId.
     *
     * @param {Number} batchId - The batch id of the batch to be retrieved.
     * @returns {Promise} - A promise that resolves to the link returned from the rest service.
     */
    function getDownloadBatchUrl(batchId) {
      return $http.get('api/batch/download', { params: { batchId } }).then((res) => res.data);
    }

    /**
     * Function used to get the most recent backup.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestBackup() {
      return $http.get('api/batch/latest').then((res) => res.data);
    }

    /**
     * Function used to get the most recent bulk restore csv verification.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestBulkRestoreCsvVerification() {
      return $http.get('api/batch/csv-verification/latest').then((res) => res.data);
    }

    /**
     * Function used to get the most recent bulk restore preview.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestBulkRestorePreview() {
      return $http.get('api/batch/bulk-restore-preview/latest').then((res) => res.data);
    }

    /**
     * Function used to get the most recent export.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestExport() {
      return $http.get('api/batch/export/latest').then((res) => res.data);
    }

    /**
     * Function used to get the most recent metadata compare.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestMetadataCompare() {
      return $http.get('api/batch/metadata-compare/latest').then((res) => res.data);
    }

    /**
     * Function used to get the backup details for an org.
     *
     * @returns {Promise} - A promise that resolve into BatchDetails
     */
    function getBackupDetails() {
      return $http.get('api/batch/details').then((res) => res.data);
    }

    /**
     * Function used to get a page of metadata restore records.
     *
     * @param {Object} params - The params object.
     * @returns {Promise} - A promise that resolve into metadata audit records
     */
    function getMetadataBatchRecords(params) {
      return $http.get('api/metadata-restore/results', { params }).then((res) => res.data);
    }

    /**
     * Function used to get the most recent object restore.
     *
     * @returns {Promise} - A promise that resolve into a BatchInfo object.
     */
    function getLatestObjectRestore() {
      return $http.get('api/batch/sobject/latest').then((res) => res.data);
    }

    /**
     * Function used to generate and download csv.
     *
     * @param {Object} params - A parameters object
     * @param {Number} params.batchId - The batch id of csv.
     *
     * TODO: Fix return object when service is done and we know what kind of object is returned
     * @returns {Promise} - A promise that resolves to an object.
     */
    function getCsv(params) {
      return $http.get('api/restore/csv', { params }).then((res) => res.data);
    }

    /**
     * Function used to get event counts for a batch.
     *
     * @param {Number} batchId - The batch id.
     * @returns {Promise} - A promise that resolves to an object.
     */
    function getEventCountForBatch(batchId) {
      return $http.get('api/batch/events/counts', { params: { batchId } }).then((res) => res.data);
    }

    /**
     * Function used to get errored records for a batch.
     *
     * @param {Object} params - The params object.
     * @returns {Promise} - A promise that resolves to an object.
     */
    function getBatchEvents(params) {
      return $http.get('api/batch/events', { params }).then((res) => res.data);
    }

    /**
     * Function used to get detailed failures for a batch-level restore error.
     *
     * @param {Object} params - The params object.
     * @returns {Promise} - A promise that resolves to an object containing a count and a result array. The count is the
     * total number of records, but the result array will only contain the current page of records.
     */
    function getBatchLevelRestoreFailures(params) {
      return $http.get('api/metadata-restore/batch-level-failures', { params }).then((res) => res.data);
    }

    /**
     * Function called to get a list of backup batch dots
     * @param {number} limit - The max number of results to be returned.
     *
     * @returns {Response|Error} - An http response object on success, an Error on failure.
     */
    function getBackupBatchDots(limit) {
      return $http
        .get('api/backup-batch-dots', { params: { limit } })
        .then((res) => res.data)
        .catch(() => ({}));
    }

    /**
     * Function called to get counts from the backup_detail table for a list of object names.
     *
     * @param {number} limit - The max number of results to be returned.
     *
     * @returns {Response|Error} - An http response object on success, an Error on failure.
     */
    function getBatchPrimaryObjectCounts(limit) {
      return $http
        .get('api/batch/primary-object-counts', { params: { limit } })
        .then((res) => res.data)
        .catch(() => ({}));
    }

    /**
     * Function called to retrieve all backups started within a particular date range.
     *
     * @param {Date} start - The start date/time, inclusive, to query for a batch.
     * @param {Date} end - The end date/time, inclusive, to query for a batch.
     *
     * @returns {Promise} - A promise that resolves into an array of batchInfo returned from the rest service.
     */
    function getBackupsByDateRange(start, end) {
      return $http
        .get('api/batches/date-range', {
          params: {
            minStartDate: start.toISOString(),
            maxStartDate: end.toISOString(),
            state: BATCH_STATES.FINISHED,
            type: 'backup'
          }
        })
        .then((res) => res.data);
    }

    /**
     * Returns backup batches that were started in the specified month. This one differs from
     * getBackupsByDateRange in that it doesn't join the details.
     *
     * @param {String} yearAndMonth - A year and month string, like "2018-06".
     * @returns {Promise} the promise of the list of BatchInfo objects for the matching batches.
     */
    function getBackupsByMonth(yearAndMonth) {
      return $http.get('api/batch/backups/month', { params: { yearAndMonth } }).then((res) => res.data);
    }

    /**
     * Send a request to create the batch cancellation request
     *
     * @param batchId - batch id
     */
    function postBatchCancellation(batchId) {
      return $http.post('api/batch-cancellations', { batchId }).then((res) => res.data);
    }
  });
